import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, use, useCallback, useMemo, useSyncExternalStore, type ReactNode } from 'react';

import * as authApi from '@/api/auth.api';
import type { LoginChallenge, LoginSession, MeResult } from '@/api/types';
import { isApiError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { tokenStore } from '@/lib/token-store';
import type { Permission } from './permissions';

type AuthContextValue = {
  user: MeResult | null;
  permissions: ReadonlySet<string>;
  /** True while the session is being restored from a stored refresh token. */
  isLoading: boolean;
  isAuthenticated: boolean;
  /**
   * Step one. Resolves to a challenge when the account needs a second factor,
   * or `null` when the password alone was enough and the session is already
   * live.
   */
  signIn: (credentials: { email: string; password: string }) => Promise<LoginChallenge | null>;
  /** Step two. Completes the challenge returned by `signIn`. */
  completeSignIn: (body: { mfaToken: string; code: string }) => Promise<void>;
  signOut: () => Promise<void>;
  can: (permission: Permission) => boolean;
  canAny: (permissions: readonly Permission[]) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  /*
   * On a page reload there is no access token in memory, only a refresh token in
   * storage. Rather than mint one eagerly, we let the /auth/me request 401 and
   * the client's refresh interceptor recover it — one code path for expiry and
   * for cold start, so there is no second implementation to keep in step.
   */
  const hasRefreshToken = useSyncExternalStore(
    tokenStore.subscribe,
    () => tokenStore.getRefreshToken() !== null,
  );

  const meQuery = useQuery({
    queryKey: queryKeys.me,
    queryFn: authApi.me,
    enabled: hasRefreshToken,
    staleTime: 5 * 60_000,
    retry: (failureCount, error) => (isApiError(error) && error.status < 500 ? false : failureCount < 2),
  });

  /**
   * Adopts a finished session.
   *
   * Shared by both paths, because a session reached through a second factor is
   * in every other respect identical to one that was not — the only difference
   * is how many round trips it took to get here.
   */
  const adopt = useCallback(
    (result: LoginSession) => {
      tokenStore.set({ accessToken: result.accessToken, refreshToken: result.refreshToken });
      // Seed rather than invalidate: login already returned the user, so a
      // second round-trip before the first render would be wasted.
      queryClient.setQueryData(queryKeys.me, {
        ...result.user,
        permissions: [],
      } satisfies MeResult);
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
    [queryClient],
  );

  const signInMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (result) => {
      /*
       * A challenge is NOT a session, and must not be adopted.
       *
       * It carries no tokens at all, so storing it would leave the panel
       * believing somebody is signed in with an `undefined` access token —
       * every subsequent request 401s and the screen looks broken rather than
       * unfinished.
       */
      if (result.mfaRequired) return;
      adopt(result);
    },
  });

  const verifyMutation = useMutation({
    mutationFn: authApi.loginVerify,
    onSuccess: adopt,
  });

  const signIn = useCallback(
    async (credentials: { email: string; password: string }) => {
      const result = await signInMutation.mutateAsync(credentials);
      return result.mfaRequired ? result : null;
    },
    [signInMutation],
  );

  const completeSignIn = useCallback(
    async (body: { mfaToken: string; code: string }) => {
      await verifyMutation.mutateAsync(body);
    },
    [verifyMutation],
  );

  const signOut = useCallback(async () => {
    const refreshToken = tokenStore.getRefreshToken();
    tokenStore.clear();
    queryClient.clear();
    // Best-effort: the local session is already gone, so a failed revoke must
    // not strand the user on a page they can no longer load.
    if (refreshToken) await authApi.logout(refreshToken).catch(() => undefined);
  }, [queryClient]);

  const user = meQuery.data ?? null;

  const permissions = useMemo<ReadonlySet<string>>(
    () => new Set(user?.permissions ?? []),
    [user?.permissions],
  );

  const value = useMemo<AuthContextValue>(() => {
    const can = (permission: Permission) => permissions.has(permission);
    return {
      user,
      permissions,
      isLoading: hasRefreshToken && meQuery.isPending,
      isAuthenticated: user !== null,
      signIn,
      completeSignIn,
      signOut,
      can,
      canAny: (list) => list.some(can),
    };
  }, [user, permissions, hasRefreshToken, meQuery.isPending, signIn, completeSignIn, signOut]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
